import CitiesServices from '@/services/cities'
import ObjectUtils from '@/utils/objectUtils'
import DOMUtils from '@/utils/DOMUtils'
import StringUtils from '@/utils/stringUtils'
import Vue from 'vue'

const LOCAL_STORAGE_KEYS =
{
    TOKEN: 'token',
    USE_LOCAL_API: 'use local API',
    USE_LOCAL_CDN: 'use local CDN',
    CITY_MANIFEST: 'city manifest',
    ACTIVE_THEME: 'active theme',
    SEND_PUSH_NOTIFS_TO_TEST: 'send push notifications to test segment',
    LAST_INSTALLED_VERSION: 'last installed version',
    LAST_ADMIN_RELEASE_NOTES_SEEN: 'last admin release notes seen',
    USE_INTENSIVE_ANIMATIONS: 'use intensive animations',
    ACTUALITIES_SEEN: 'actualities seen',
    EVENTS_SEEN: 'events seen',
    LAST_NOTIFICATION_SEEN: 'last notification seen',
    FAVORITES: 'favorites',
    NOTIFICATION_SETTINGS: 'notification settings',
    USER_UUID: 'user UUID',
    REMOTE_SCRIPTS_EXECUTED: 'remote scripts executed',
    COVID_CERTIFICATE_TEMPLATES: 'COVID certificate templates',
    CONFIG_OVERWRITE: 'config overwrite',
    CLOSED_ALERTS_IDS: 'closed alerts IDs',
}

const localStorage = 
{
    setStringItem (key, value)
    {
        const isErasingToken = value == null
        if (typeof value != 'string' && !isErasingToken)
        {
            throw new Error('Value of', key, 'must be of type string, gived: "', value, '"')
        }
        
        window.localStorage.setItem(key, value)
    },

    setBoolItem (key, value)
    {
        const isErasingToken = value == null
        if (typeof value != 'boolean' && !isErasingToken)
        {
            throw new Error('Value must of', key, 'be of type boolean, gived:', value)
        }
        
        window.localStorage.setItem(key, value)
    },

    setArrayItem (key, value)
    {
        const isErasingToken = value == null
        if (!Array.isArray(value) && !isErasingToken)
        {
            throw new Error('Value must of', key, 'be of type array, gived:', value)
        }
        
        window.localStorage.setItem(key, JSON.stringify(value))
    },

    setObjectItem (key, value)
    {
        const isErasingToken = value == null
        if (!ObjectUtils.isObject(value) && !isErasingToken)
        {
            throw new Error('Value must of', key, 'be of type object, gived:', value)
        }
        
        window.localStorage.setItem(key, JSON.stringify(value))
    },

    /**
     * 
     * @returns {String}
     */
    getStringItem (key)
    {
        let value = window.localStorage.getItem(key)
        if (value == null || value == 'null')
        {
            value = null
        }

        return value
    },

    /**
     * 
     * @returns {Boolean}
     */
    getBoolItem (key)
    {
        let value = window.localStorage.getItem(key)
        if (value == null || value == 'null')
        {
            value = null
        }
        else
        {
            value = ((window.localStorage.getItem(key) || false).toString() == true.toString())
        }

        return value
    },

    /**
     * 
     * @returns {Array}
     */
    getArrayItem (key)
    {
        let value = window.localStorage.getItem(key)
        if (value == null || value == 'null')
        {
            value = null
        }
        else
        {
            value = JSON.parse(window.localStorage.getItem(key))
        }

        return value
    },

    /**
     * 
     * @returns {Object}
     */
    getObjectItem (key)
    {
        let value = window.localStorage.getItem(key)
        if (value == null || value == 'null' || value == undefined || value == 'undefined')
        {
            value = null
        }
        else
        {
            var val = window.localStorage.getItem(key)
            if (!val.includes('{') || !val.includes('}')) val = null

            return JSON.parse(val)
        }

        return value
    },

    /**
     * 
     * @param {string} cityId 
     */
    fetchCityManifest (cityId)
    {
        return new Promise((resolve, reject) =>
        {
            CitiesServices.getCityManifest(cityId).then(manifest =>
            {
                manifest.requestedFrom = cityId

                this.cityManifest = JSON.stringify(manifest)
        
                return resolve(manifest)
            })
            .catch(reject)
        })
    },

    set cityManifest (value)
    { 
        this.setStringItem(LOCAL_STORAGE_KEYS.CITY_MANIFEST, value)
    },
    get cityManifest ()
    {
        const fromLocalStorage = window.localStorage.getItem(LOCAL_STORAGE_KEYS.CITY_MANIFEST)

        if (fromLocalStorage && fromLocalStorage != 'null')
        {
            return JSON.parse(fromLocalStorage)
        }
        else throw new Error('City manifest has not been fetched yet')
    },

    set token (value)
    { 
        this.setStringItem(LOCAL_STORAGE_KEYS.TOKEN, value)
    },
    get token ()
    {
        return this.getStringItem(LOCAL_STORAGE_KEYS.TOKEN)
    },

    set useLocalApi (value)
    {    
        this.setBoolItem(LOCAL_STORAGE_KEYS.USE_LOCAL_API, value)
    },
    get useLocalApi ()
    {
        return this.getBoolItem(LOCAL_STORAGE_KEYS.USE_LOCAL_API)
    },
    
    set useLocalCdn (value)
    {    
        this.setBoolItem(LOCAL_STORAGE_KEYS.USE_LOCAL_CDN, value)
    },
    get useLocalCdn ()
    {
        return this.getBoolItem(LOCAL_STORAGE_KEYS.USE_LOCAL_CDN)
    },

    set sendPushNotifsToTest (value)
    {    
        this.setBoolItem(LOCAL_STORAGE_KEYS.SEND_PUSH_NOTIFS_TO_TEST, value)
    },
    get sendPushNotifsToTest ()
    {
        return this.getBoolItem(LOCAL_STORAGE_KEYS.SEND_PUSH_NOTIFS_TO_TEST)
    },

    set activeTheme (value)
    {   
        this.setStringItem(LOCAL_STORAGE_KEYS.ACTIVE_THEME, value)
    },
    get activeTheme ()
    {
        return this.getStringItem(LOCAL_STORAGE_KEYS.ACTIVE_THEME)
    },

    set lastInstalledVersion (value)
    {   
        this.setStringItem(LOCAL_STORAGE_KEYS.LAST_INSTALLED_VERSION, value)
    },
    get lastInstalledVersion ()
    {
        return this.getStringItem(LOCAL_STORAGE_KEYS.LAST_INSTALLED_VERSION)
    },

    set lastAdminReleaseNotesSeen (value)
    {   
        this.setStringItem(LOCAL_STORAGE_KEYS.LAST_ADMIN_RELEASE_NOTES_SEEN, value)
    },
    get lastAdminReleaseNotesSeen ()
    {
        return this.getStringItem(LOCAL_STORAGE_KEYS.LAST_ADMIN_RELEASE_NOTES_SEEN)
    },

    set useIntensiveAnimations (value)
    {   
        this.setBoolItem(LOCAL_STORAGE_KEYS.USE_INTENSIVE_ANIMATIONS, value)
    },
    get useIntensiveAnimations ()
    {
        let value = this.getBoolItem(LOCAL_STORAGE_KEYS.USE_INTENSIVE_ANIMATIONS)
        if (value == null)
        {
            // Chrome Blink engine sucks for low-end mobile animations ._.
            const isLowEndMobile = DOMUtils.getMatchingMediaName() == 'mobile' && !DOMUtils.isLargeMobile()
            value = !(cordova.platformId == 'android' && isLowEndMobile)
        }

        return value
    },

    set actualitiesSeen (value)
    {   
        this.setArrayItem(LOCAL_STORAGE_KEYS.ACTUALITIES_SEEN, value)
    },
    get actualitiesSeen ()
    {
        return this.getArrayItem(LOCAL_STORAGE_KEYS.ACTUALITIES_SEEN) || []
    },

    set eventsSeen (value)
    {   
        this.setArrayItem(LOCAL_STORAGE_KEYS.EVENTS_SEEN, value)
    },
    get eventsSeen ()
    {
        return this.getArrayItem(LOCAL_STORAGE_KEYS.EVENTS_SEEN) || []
    },

    set favorites (value)
    {   
        this.setObjectItem(LOCAL_STORAGE_KEYS.FAVORITES, value)
    },
    get favorites ()
    {
        return this.getObjectItem(LOCAL_STORAGE_KEYS.FAVORITES)
    },

    // set notificationSettings (value)
    // {   
    //     // this.setObjectItem(LOCAL_STORAGE_KEYS.NOTIFICATION_SETTINGS, value)
    // },
    // get notificationSettings ()
    // {
    //     const settings = Object.assign(
    //     {
    //         notifiedOnImportantAlert: true,
    //         notifiedOnCommunication: true,
    //         notifiedOnNewActuality: true,
    //         notifiedOnNewEvent: true,
    //         notifiedOnNewPublishedLostObject: true,
    //         notifiedOnNewPublishedLostPet: false,
    //     }, this.getObjectItem(LOCAL_STORAGE_KEYS.NOTIFICATION_SETTINGS) || {})


    //     const user = window.app.$store.state.user
    //     if (user.isLogged)
    //     {
    //         // is not null & equal to true & has right to receive this notification
    //         settings.notifiedOnNewReport = (settings.notifiedOnNewReport != false) && !!user.privileges.canManageReports
    //         settings.notifiedOnNewLostObject = (settings.notifiedOnNewLostObject != false) && !!user.privileges.canManageLostObjects
    //         settings.notifiedOnNewLostPet = (settings.notifiedOnNewLostPet != false) && !!user.privileges.canManageLostPets
    //     }

    //     this.notificationSettings = settings

    //     return settings
    // },

    set userUuid (value)
    {   
        this.setStringItem(LOCAL_STORAGE_KEYS.USER_UUID, value)
    },
    get userUuid ()
    {
        var uuid = this.getStringItem(LOCAL_STORAGE_KEYS.USER_UUID)
        if (!uuid)
        {
            if (window.device && device.uuid) uuid = device.uuid
            else uuid = StringUtils.generateUuid()

            this.userUuid = uuid
        }

        return uuid
    },

    set remoteScriptsExecuted (value)
    {   
        this.setArrayItem(LOCAL_STORAGE_KEYS.REMOTE_SCRIPTS_EXECUTED, value)
    },
    get remoteScriptsExecuted ()
    {
        const ids = this.getArrayItem(LOCAL_STORAGE_KEYS.REMOTE_SCRIPTS_EXECUTED) || []

        return ids.filter(id => id)
    },

    set covidCertificateTemplates (value)
    {   
        this.setArrayItem(LOCAL_STORAGE_KEYS.COVID_CERTIFICATE_TEMPLATES, value)
    },
    get covidCertificateTemplates ()
    {
        return this.getArrayItem(LOCAL_STORAGE_KEYS.COVID_CERTIFICATE_TEMPLATES) || []
    },

    set configOverwrite (value)
    {   
        this.setObjectItem(LOCAL_STORAGE_KEYS.CONFIG_OVERWRITE, value)
    },
    get configOverwrite ()
    {
        return this.getObjectItem(LOCAL_STORAGE_KEYS.CONFIG_OVERWRITE) || {}
    },

    set closedAlertIds (value)
    {   
        this.setArrayItem(LOCAL_STORAGE_KEYS.CLOSED_ALERTS_IDS, value)
    },
    get closedAlertIds ()
    {
        return this.getArrayItem(LOCAL_STORAGE_KEYS.CLOSED_ALERTS_IDS) || []
    },
}

localStorage.observables = Vue.observable({
    set lastNotificationSeen (value)
    {   
        localStorage.setObjectItem(LOCAL_STORAGE_KEYS.LAST_NOTIFICATION_SEEN, value)
    },
    get lastNotificationSeen ()
    {
        return localStorage.getObjectItem(LOCAL_STORAGE_KEYS.LAST_NOTIFICATION_SEEN)
    },
})

export default localStorage


// WEBPACK FOOTER //
// ./src/utils/localStorageUtils.js